// src/components/copilot/AgUiBorrowerAssistantChat.jsx
import { Assistant } from "@mui/icons-material";
import { Box, Drawer, IconButton, Paper, Tooltip } from "@mui/material";
import { isEmpty } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ChatHeader from "./components/ChatHeader";
import ChatInput from "./components/ChatInput";
import ChatMessages from "./components/ChatMessages";
import { useMockChatStream } from "./hooks/useMockChatStream";

const AgUiBorrowerAssistantChat = () => {
  const profile = useSelector((state) => state.profile);
  const financingRequests = useSelector((state) => state.financingRequests);

  const [open, setOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState("compact"); // 'compact', 'expanded', 'inline'

  const profileContext = useMemo(() => {
    return {
      hasEntities: !isEmpty(profile?.associatedEntities),
      entitiesCount: profile?.associatedEntities?.length || 0,
      hasExperience: !isEmpty(profile?.validatedExperience),
      experienceCount: profile?.validatedExperience?.length || 0,
      hasDocuments: !isEmpty(profile?.borrowerDocuments),
      documentsCount: profile?.borrowerDocuments?.length || 0,
      hasBankAccounts: !isEmpty(profile?.bankAccounts),
      bankAccountsCount: profile?.bankAccounts?.length || 0,
      totalLoans: financingRequests?.length || 0,
      loansWithConditions:
        financingRequests?.filter(
          (loan) => loan.underwritingRequirements?.length > 0,
        ).length || 0,
    };
  }, [profile, financingRequests]);

  const { messages, input, setInput, isLoading, error, setError, sendMessage } =
    useMockChatStream(profileContext);

  const handleClose = useCallback(() => {
    setOpen(false);
    setDisplayMode("compact");
  }, []);

  const handleOpen = useCallback(() => setOpen(true), []);

  const handleCycleDisplayMode = useCallback(() => {
    setDisplayMode((prev) => {
      if (prev === "compact") return "expanded";
      if (prev === "expanded") return "inline";
      return "compact";
    });
  }, []);

  const handleSaveStructuredData = useCallback(
    ({ data, schema, className }) => {
      console.log("Saving structured data:", { data, schema, className });

      switch (className) {
        case "Entity":
          console.log("Would dispatch addEntity with:", data);
          break;

        case "Experience":
          console.log("Would dispatch addExperience with:", data);
          break;

        case "BankAccount":
          console.log("Would dispatch addBankAccount with:", data);
          break;

        default:
          console.warn("Unknown class name:", className);
      }
    },
    [],
  );

  const drawerPaperProps = useMemo(() => {
    if (displayMode === "inline") {
      return {
        sx: {
          height: "100vh",
          position: "relative",
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
          overflow: "hidden",
          right: 0,
          width: { xs: "100%", sm: 450 },
          maxWidth: "100%",
          transition: "all 0.3s ease-in-out",
        },
      };
    }

    const isExpanded = displayMode === "expanded";
    return {
      sx: {
        maxHeight: isExpanded ? "100vh" : "64vh",
        height: "100%",
        minHeight: "700px",
        left: "unset",
        borderTopLeftRadius: isExpanded ? 0 : 16,
        borderTopRightRadius: isExpanded ? 0 : 16,
        overflow: "hidden",
        right: isExpanded ? 0 : 36,
        width: { xs: "100%", sm: isExpanded ? 600 : 520 },
        maxWidth: "100%",
        transition: "all 0.3s ease-in-out",
      },
    };
  }, [displayMode]);

  // Access control
  if (
    profile?.emailAddress !== "cwolpert@cliffcomortgage.com" &&
    profile?.emailAddress !== "joliva@cliffcomortgage.com" &&
    profile?.emailAddress !== "olivajames110@gmail.com" &&
    profile?.emailAddress !== "jolivacliffcomortgage@gmail.com"
  ) {
    return null;
  }

  const chatContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "grey.50",
      }}
    >
      <ChatHeader
        onClose={handleClose}
        displayMode={displayMode}
        onCycleDisplayMode={handleCycleDisplayMode}
      />

      <ChatMessages
        messages={messages}
        isLoading={isLoading}
        error={error}
        onErrorClose={() => setError(null)}
        profileContext={profileContext}
        onSendMessage={sendMessage}
        onSaveStructuredData={handleSaveStructuredData}
      />

      <ChatInput
        input={input}
        setInput={setInput}
        isLoading={isLoading}
        onSendMessage={sendMessage}
      />
    </Box>
  );

  return (
    <>
      {!open && (
        <Tooltip title="Open Loan Assistant" placement="top">
          <IconButton
            size="large"
            onClick={handleOpen}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 36,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              p: 2,
              boxShadow: 3,
              zIndex: 1000,
              rotate: "270deg",
              transition: "background-color 0.3s, transform 0.3s",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            <Assistant
              sx={{
                color: "#ffffff",
                fontSize: "36px",
                transform: "rotate(270deg)",
              }}
            />
          </IconButton>
        </Tooltip>
      )}

      {displayMode === "inline" && open ? (
        // Inline mode - fixed positioned panel
        <Paper
          elevation={3}
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: { xs: "100%", sm: 450 },
            height: "100vh",
            zIndex: 1200,
            borderRadius: 0,
            overflow: "hidden",
          }}
        >
          {chatContent}
        </Paper>
      ) : (
        // Drawer mode (compact or expanded)
        <Drawer
          anchor="bottom"
          open={open}
          hideBackdrop
          onClose={handleClose}
          PaperProps={drawerPaperProps}
        >
          {chatContent}
        </Drawer>
      )}
    </>
  );
};

export default AgUiBorrowerAssistantChat;
